import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from '@/providers/i18n';

import type { QuizPlayerQuestionProps as QuizQuestionProps } from './types';

import { cn } from '../../lib/utils';

type QuizPlayerQuestionProps = React.ComponentProps<'div'> & QuizQuestionProps;

export function QuizPlayerQuestion({
  question,
  choices,
  selectedAnswer,
  isAnswerValidated,
  correctAnswer,
  hint,
  isCorrect,
  title,
  onSelectAnswer = () => {},
  className,
  ...others
}: QuizPlayerQuestionProps) {
  return (
    <div
      data-slot="quiz-player-question"
      className={cn('flex flex-col gap-6 text-card-foreground', className)}
      {...others}
    >
      <div className="font-semibold leading-none tracking-tight text-xl">
        {/* <div className="text-sm mb-2 text-muted-foreground">
          {title}
        </div> */}
        {question}
      </div>
      <div className="*:mb-3 *:last:mb-0">
        {choices.map((choice) => (
          <QuizPlayerChoice
            key={choice.label}
            selected={selectedAnswer === choice.value}
            disabled={isAnswerValidated}
            isCorrect={isAnswerValidated && choice.value === correctAnswer}
            isWrong={
              isAnswerValidated && choice.value === selectedAnswer && !isCorrect
            }
            onClick={() => onSelectAnswer(choice.value)}
          >
            {choice.label}
          </QuizPlayerChoice>
        ))}
      </div>

      {/* <div className="flex items-center">
        {hint && (
          <QuizPlayerHint
            className={
              isAnswerValidated
                ? 'opacity-0 pointer-events-none'
                : undefined
            }
          >
            {hint}
          </QuizPlayerHint>
        )} */}
      {/* {isAnswerValidated && <QuizPlayerFeedback isCorrect={isCorrect} />} */}
      {/* </div> */}
    </div>
  );
}

type QuizPlayerChoiceProps = React.ComponentProps<'button'> & {
  selected?: boolean;
  disabled?: boolean;
  isCorrect?: boolean;
  isWrong?: boolean;
  onClick?: () => void;
};

export function QuizPlayerChoice({
  selected,
  disabled,
  isCorrect,
  isWrong,
  onClick = () => {},
  children,
  className,
  ...others
}: QuizPlayerChoiceProps) {
  const baseClasses =
    'w-full p-4 rounded-lg bg-transparent border-border border-2 text-foreground text-left transition-all duration-200';

  const getStateClasses = () => {
    if (isCorrect)
      return 'border-success bg-success/10 text-success font-semibold';
    if (isWrong)
      return 'border-destructive bg-destructive/10 text-destructive font-semibold';
    if (selected) return 'border-primary bg-primary/10 text-primary';
    if (disabled) return 'text-muted';
    return 'hover:bg-muted cursor-pointer';
  };

  return (
    <button
      {...others}
      className={cn(baseClasses, getStateClasses(), className)}
      onClick={onClick}
      disabled={disabled}
      data-slot="quiz-player-choice"
      data-state={
        isCorrect ? 'correct' : isWrong ? 'wrong' : selected ? 'selected' : ''
      }
    >
      {children}
    </button>
  );
}

export type QuizPlayerHintProps = React.ComponentProps<'button'> & {};

export function QuizPlayerHint({
  children,
  className,
  ...others
}: QuizPlayerHintProps) {
  const { t } = useTranslation();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          {...others}
          asChild
          data-slot="quiz-player-hint-trigger"
          className={cn(
            'text-muted-foreground/50 text-sm transition-all',
            className
          )}
        >
          <div>
            <HelpCircle size={16} className="inline mr-2" />
            {t('quizHint', 'Suggerimento')}
          </div>
        </TooltipTrigger>
        <TooltipContent
          className="bg-popover text-popover-foreground"
          side="top"
          align="start"
          data-slot="quiz-player-hint-content"
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function QuizPlayerFeedback({
  isCorrect,
}: React.ComponentProps<'div'> & { isCorrect?: boolean }) {
  return (
    <div
      data-slot="quiz-player-feedback"
      className={`p-4 rounded-lg ${
        isCorrect ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
      }`}
    >
      {isCorrect ? 'Correct!' : 'Incorrect. Try again!'}
    </div>
  );
}
